import React, { useEffect, useRef, useState } from "react";
import type { Match } from "../../../shared/types";

import GameCanvas from "../../../game-canvas/GameCanvas";
import Score from "../game/Score";
import GameHud from "../game/GameHud";
import { matchPlaystyle } from "../../setup/presets";
import GameLayout from "../game/GameLayout";
import TouchSteeringOverlay from "../game/TouchSteeringOverlay";
import PlayerSteeringListener from "../game/player-steering-listener.js";
import requestFrame from "../../../shared/request-frame.js";
import * as scoreUtil from "core/src/core/score/score-util.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { wormColors } from "core/src/core/constants.js";
import Overlay from "../../../game-canvas/overlays/Overlay";
import RoundOverCard, { contentFadeFor } from "../round-over/RoundOverCard";
import { isTieBreak } from "../../../shared/tie-break";
import musicController from "../../../audio/music-controller";
import { computeGameAudio } from "../../../audio/game-audio";
import { updateLocalSetupPlayers } from "../../../shared/local-setup-store";
import { useIsTouch } from "../../../shared/useResponsive";
import styles from "../play.module.scss";
import { roundWinners } from "../../../shared/round-winners";

// Online matches march on by themselves: when a round ends everyone watches the
// winner for a few seconds and is then taken to the next round automatically.
const NEXT_ROUND_SECONDS = 5;

interface Props {
  match: Match;
  // Threaded by App but unused here (GameCanvas doesn't read it).
  overlay?: any;
  // Offline: the live game from the finished round, kept running as a cosmetic victory lap
  // during this screen's countdown (the winner + bots keep driving). Absent online / on the
  // match-over screen - then we just show the frozen round-end state.
  lapGame?: any;
  onStartNextGameAction?: () => void;
  onMatchOverAction?: () => void;
  onLeaveAction?: () => void;
  isLocal?: boolean;
}

export default function GameOver(props: Props) {
  const { match, isLocal, lapGame } = props;
  const touch = useIsTouch();
  const propsRef = useRef(props);
  propsRef.current = props;

  // Victory lap: run the handed-over game in core "victory mode" for the countdown, so the
  // winner + bots keep driving (and can pick up power-ups - twin included - and crash), all
  // cosmetic. GameCanvas re-reads the mutating gameState each frame, so we just advance the
  // sim here and bind the human winner's steering. Skipped online / when there's no lapGame.
  useEffect(() => {
    // (a Respawn round ends on its timer with everyone in: the board just stops)
    if (!lapGame || lapGame.gameState.respawn) return;
    lapGame.enterVictoryLap();
    const steering = PlayerSteeringListener();
    match.matchConfig.players.forEach((player: any) => {
      if (player.type === "human") {
        steering.addKeyListeners({
          left: player.left,
          right: player.right,
          onSteeringUpdate: (s: number) => gsf.setPlayerSteering(lapGame.gameState, player.id, s),
        });
      }
    });
    // Drive the crash / pickup SFX off the live lap game, so grabbing a power-up
    // (twin included) or crashing on the lap still sounds. The SFX loop keys off
    // cumulative gameState event counts, so sync the baseline on the first frame
    // (suppressSfx) - otherwise the finished round's grabs / deaths retro-fire; only
    // new lap events sound. Music between rounds stays off (view isn't "audible").
    let audioSynced = false;
    musicController.setGameAudioProvider(() => {
      const audio = computeGameAudio(lapGame.gameState);
      if (audio && !audioSynced) {
        audioSynced = true;
        return { ...audio, suppressSfx: true };
      }
      return audio;
    });
    let running = true;
    let prev = Date.now();
    function loop() {
      if (!running) return;
      const now = Date.now();
      lapGame.update((now - prev) / 1000);
      prev = now;
      requestFrame(loop);
    }
    requestFrame(loop);
    return () => {
      running = false;
      steering.removeKeyListeners();
      musicController.setGameAudioProvider(null);
      lapGame.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [, force] = useState(0);
  const forceRender = () => force((t) => t + 1);

  const [secondsLeft, setSecondsLeftState] = useState(NEXT_ROUND_SECONDS);
  const secondsLeftRef = useRef(NEXT_ROUND_SECONDS);
  function setSecondsLeft(v: number) {
    secondsLeftRef.current = v;
    setSecondsLeftState(v);
  }
  // Any toolbar modal incl. the leave-confirm (owned by GameLayout) pauses the
  // auto-advance countdown; GameLayout reports it via onModalOpenChange.
  const modalOpenRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false); // also holds the board's content fade
  const advancedRef = useRef(false);
  const tickIdRef = useRef<any>(null);

  function advance() {
    if (advancedRef.current) {
      return;
    }
    advancedRef.current = true;
    const p = propsRef.current;
    if (!p.match.isMatchOver()) {
      p.onStartNextGameAction?.();
    } else {
      p.onMatchOverAction?.();
    }
  }

  function leaveMatch() {
    advancedRef.current = true; // stop the auto-advance from also firing
    clearInterval(tickIdRef.current);
    propsRef.current.onLeaveAction?.();
  }

  // Rebinding between rounds just updates the match config; the next round's game
  // component reads it when it sets up steering (nothing live to re-bind).
  function changePlayerKey(playerId: any, side: string, newKey: string) {
    const players = match.matchConfig.players;
    players.forEach((p: any) => {
      if (p.type === "human") {
        if (p.left === newKey) p.left = null;
        if (p.right === newKey) p.right = null;
      }
    });
    const target = players.find((p: any) => p.id === playerId);
    if (target) {
      if (side === "left") target.left = newKey;
      else target.right = newKey;
    }
    if (isLocal) updateLocalSetupPlayers(players);
    forceRender();
  }

  useEffect(() => {
    function tick() {
      if (modalOpenRef.current) {
        return; // hold the countdown while a dialog (leave / settings / help) is open
      }
      const next = secondsLeftRef.current - 1;
      if (next <= 0) {
        clearInterval(tickIdRef.current);
        setSecondsLeft(0);
        advance();
      } else {
        musicController.playSoftTick(); // soft ticks as the counter ticks 4..1
        setSecondsLeft(next);
      }
    }
    tickIdRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickIdRef.current);
  }, []);

  const matchOver = match.isMatchOver();
  const roundData = match.matchState.roundsData[match.matchState.roundsData.length - 1];

  // Defensive: this screen must NEVER take the whole app down. A live black-screen crash
  // (2026-07-17, hosted /play game on production) hit exactly this: game-over rendered while
  // roundsData was still empty, and the roundData dereferences below threw. If the round's
  // data isn't there, show a minimal round-over card (the countdown still advances the flow)
  // instead of crashing the tree to black.
  if (!roundData || !roundData.gameState) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "1rem" }}>
        <h1>Round over</h1>
        <div className={styles.gameOverCountdown}>
          {matchOver ? "Final results in" : "Next round in"}
          <span className={styles.gameOverCountdownNum}>{secondsLeft}</span>
        </div>
        <button className="app-btn app-btn-ghost" onClick={leaveMatch}>Leave</button>
      </div>
    );
  }

  const roundScore = scoreUtil.calculateRoundScore(roundData.gameState, roundData.gameState.gameTime);
  const sortedScore = scoreUtil.createSortedList(roundScore);
  const roundWinningPlayer = match.matchConfig.players.find((p: any) => p.id === sortedScore[0].id);

  const canvasConfig = { fullscreen: true };
  const players = match.matchConfig.players;
  const humans = players.filter((p: any) => p.type === "human");
  const youIds = humans.map((p: any) => p.id);
  const roundNumber = match.matchState.roundsData.length;
  // The top is shared past the target: this round didn't settle it.
  const tieBreak = !matchOver && isTieBreak(match.getCurrentScore(), match.matchConfig.maxScore, match.matchConfig.maxRounds, roundNumber);

  // The locked B2 round-over card: round winner + podium strip + countdown. On
  // the final round it counts down to the results instead of a next round.
  const canvasOverlay = (
    <Overlay gameState={roundData.gameState} className="canvas-overlay-text canvas-overlay-above-wall">
      <RoundOverCard
        winnerName={roundWinningPlayer.name}
        winnerColorId={roundWinningPlayer.colorId}
        winnerFlag={roundWinningPlayer.flag}
        roundNumber={roundNumber}
        roundPoints={sortedScore[0].score}
        final={matchOver}
        secondsLeft={secondsLeft}
        totalSeconds={NEXT_ROUND_SECONDS}
        paused={modalOpen}
        tie={tieBreak}
      />
    </Overlay>
  );

  return (
    <GameLayout
      hud={
        <GameHud
          gameState={roundData.gameState}
          roundNumber={roundNumber}
          totalPlayers={players.length}
          maxScore={match.matchConfig.maxScore}
          maxRounds={match.matchConfig.maxRounds}
          players={players}
          winners={roundWinners(match.matchState.roundsData)}
        />
      }
      arena={
        <GameCanvas config={canvasConfig} gameState={lapGame ? lapGame.gameState : roundData.gameState} players={players} contentFade={matchOver ? undefined : contentFadeFor(NEXT_ROUND_SECONDS, modalOpen)}>
          {canvasOverlay}
        </GameCanvas>
      }
      score={
        <Score
          gameState={roundData.gameState}
          players={players}
          startScore={roundData.startScore}
          maxScore={match.matchConfig.maxScore}
          maxRounds={match.matchConfig.maxRounds}
          roundOver={true}
          tieBreak={tieBreak}
          showTrophys={matchOver}
          youIds={youIds}
          youKeys={Object.fromEntries(humans.map((p: any) => [p.id, touch ? null : { left: p.left, right: p.right }]))}
        />
      }
      map={roundData.gameState.map}
      playstyle={matchPlaystyle(match.matchConfig)}
      onLeave={leaveMatch}
      roundOver={true}
      roundOverTiming={{ totalSeconds: NEXT_ROUND_SECONDS, paused: modalOpen }}
      onModalOpenChange={(open: boolean) => {
        modalOpenRef.current = open;
        setModalOpen(open);
      }}
      settingsPlayers={humans}
      onChangeKey={changePlayerKey}
      // The lap is steerable on a phone too, through the same touch zones as
      // the round (the keys are bound in the lap effect above).
      touchControls={
        touch && lapGame ? (
          <TouchSteeringOverlay players={players} onSteeringUpdate={(playerId: any, s: number) => gsf.setPlayerSteering(lapGame.gameState, playerId, s)} />
        ) : undefined
      }
    />
  );
}
